import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// AddressField archetype — Mapbox-autofill address with mailing-same
// toggle. Behavior ported from apps/portal/src/signup/steps/AddressStep.tsx.
//
// Mapbox autofill enablement is host-provided via `adapters.enableMapboxAutofill`
// — the script tag lives in the portal's index.signup.html. When the
// adapter is unset the fields still work as plain inputs.
//
// Location-key resolution is also host-provided via `adapters.resolveLocationKey`.
// When resolution succeeds the key is folded into the field's value payload
// so downstream PlanPicker can use it.
//
// Configuration:
//   - payloadKey       → object path on the draft, e.g. "serviceAddress"
//   - mailingPayloadKey→ object path for mailing address, e.g. "mailingAddress"
//   - mailingToggleKey → flag path for the same-as-service flag
//   - locationKeyPath  → optional path to store resolved location key
//   - country          → ISO code passed to Mapbox autofill (default 'us')
//   - showMailing      → whether to render the mailing toggle + fields
import { useEffect, useRef, useState } from 'react';
import { useStageForm } from '../runtime/useStageForm.js';
import { s, b } from './common.js';
const EMPTY = { line1: '', line2: '', city: '', state: '', postalCode: '' };
function readAddress(payload, path) {
    const parts = path.split('.');
    let cur = payload;
    for (const part of parts) {
        if (cur && typeof cur === 'object' && part in cur) {
            cur = cur[part];
        }
        else {
            cur = undefined;
            break;
        }
    }
    if (cur && typeof cur === 'object') {
        const a = cur;
        return {
            line1: typeof a.line1 === 'string' ? a.line1 : '',
            line2: typeof a.line2 === 'string' ? a.line2 : '',
            city: typeof a.city === 'string' ? a.city : '',
            state: typeof a.state === 'string' ? a.state : '',
            postalCode: typeof a.postalCode === 'string' ? a.postalCode : '',
        };
    }
    return { ...EMPTY };
}
function readBoolPath(payload, path, fallback) {
    const parts = path.split('.');
    let cur = payload;
    for (const part of parts) {
        if (cur && typeof cur === 'object' && part in cur) {
            cur = cur[part];
        }
        else
            return fallback;
    }
    return typeof cur === 'boolean' ? cur : fallback;
}
function isValid(a) {
    return Boolean(a.line1.trim() &&
        a.city.trim() &&
        /^[A-Za-z]{2}$/.test(a.state) &&
        /^\d{5}$/.test(a.postalCode));
}
export function AddressFieldComponent(props) {
    const { config, payload, registry, blockId, adapters } = props;
    const payloadKey = s(config, 'payloadKey', 'serviceAddress');
    const mailingPayloadKey = s(config, 'mailingPayloadKey', 'mailingAddress');
    const mailingToggleKey = s(config, 'mailingToggleKey', 'mailingSameAsService');
    const locationKeyPath = s(config, 'locationKeyPath', 'locationKey');
    const showMailing = b(config, 'showMailing', true);
    const [service, setService] = useState(() => {
        const initial = readAddress(payload, payloadKey);
        if (initial.line1)
            return initial;
        return { line1: '', line2: '', city: 'Golden', state: 'CO', postalCode: '80403' };
    });
    const [mailing, setMailing] = useState(() => readAddress(payload, mailingPayloadKey));
    const [sameAsService, setSameAsService] = useState(() => readBoolPath(payload, mailingToggleKey, true));
    const formRef = useRef(null);
    // Mapbox autofill enablement — adapter-provided so the package doesn't
    // depend on the portal-specific script tag in index.signup.html.
    useEffect(() => {
        if (!adapters.enableMapboxAutofill || !formRef.current)
            return;
        return adapters.enableMapboxAutofill(formRef.current);
    }, [adapters]);
    // Register the composite value with the stage form. We bundle the
    // address + mailing + flag into a single registry entry under the
    // root payloadKey, and store the related fields beside via overrides
    // on the patch object — this keeps the patch shape the same as the
    // legacy AddressStep produced.
    useStageForm(registry, blockId, {
        payloadKey,
        value: isValid(service) ? service : undefined,
        validator: (v) => (v === undefined ? 'Please enter a complete service address.' : null),
    });
    // Also register the mailing + flag + locationKey resolver behind
    // dedicated registry slots so the patch the host produces carries
    // them. Each registers under a unique blockId suffix so they don't
    // collide.
    useStageForm(registry, `${blockId}::mailing-flag`, {
        payloadKey: mailingToggleKey,
        value: sameAsService,
    });
    useStageForm(registry, `${blockId}::mailing`, {
        payloadKey: mailingPayloadKey,
        value: sameAsService ? service : mailing,
    });
    // Async location-key resolution — fire when the address looks valid.
    // Result goes through a separate `useStageForm` slot. We update via
    // a state hold-cell so the registry's value stays fresh.
    const [resolvedKey, setResolvedKey] = useState(null);
    useEffect(() => {
        let cancelled = false;
        if (!adapters.resolveLocationKey || !isValid(service))
            return;
        void adapters
            .resolveLocationKey({
            line1: service.line1,
            city: service.city,
            state: service.state,
            postalCode: service.postalCode,
        })
            .then((key) => {
            if (!cancelled && key)
                setResolvedKey(key);
        })
            .catch(() => {
            /* resolver may not be reachable in dev — degrade gracefully */
        });
        return () => {
            cancelled = true;
        };
    }, [adapters, service.line1, service.city, service.state, service.postalCode]);
    useStageForm(registry, `${blockId}::location-key`, {
        payloadKey: locationKeyPath,
        value: resolvedKey ?? undefined,
    });
    return (_jsxs("form", { ref: formRef, className: "flex flex-col gap-4", onSubmit: (e) => e.preventDefault(), children: [_jsxs("label", { className: "flex flex-col gap-1.5", children: [_jsx("span", { className: "text-xs font-semibold", children: "Service address *" }), _jsx("input", { name: "address1", autoComplete: "address-line1", placeholder: "Start typing your address\u2026", className: "w-full rounded-md border bg-white px-3 py-2 text-sm", style: { borderColor: 'var(--bcbb-border)' }, value: service.line1, onChange: (e) => setService((a) => ({ ...a, line1: e.target.value })), required: true })] }), _jsxs("label", { className: "flex flex-col gap-1.5", children: [_jsx("span", { className: "text-xs font-semibold", children: "Address line 2" }), _jsx("input", { name: "address2", autoComplete: "address-line2", className: "w-full rounded-md border bg-white px-3 py-2 text-sm", style: { borderColor: 'var(--bcbb-border)' }, value: service.line2 ?? '', onChange: (e) => setService((a) => ({ ...a, line2: e.target.value })) })] }), _jsxs("div", { className: "grid grid-cols-1 gap-4 md:grid-cols-3", children: [_jsxs("label", { className: "flex flex-col gap-1.5", children: [_jsx("span", { className: "text-xs font-semibold", children: "City *" }), _jsx("input", { autoComplete: "address-level2", className: "w-full rounded-md border bg-white px-3 py-2 text-sm", style: { borderColor: 'var(--bcbb-border)' }, value: service.city, onChange: (e) => setService((a) => ({ ...a, city: e.target.value })), required: true })] }), _jsxs("label", { className: "flex flex-col gap-1.5", children: [_jsx("span", { className: "text-xs font-semibold", children: "State *" }), _jsx("input", { autoComplete: "address-level1", className: "w-full rounded-md border bg-white px-3 py-2 text-sm", style: { borderColor: 'var(--bcbb-border)' }, value: service.state, onChange: (e) => setService((a) => ({ ...a, state: e.target.value.toUpperCase() })), maxLength: 2, required: true })] }), _jsxs("label", { className: "flex flex-col gap-1.5", children: [_jsx("span", { className: "text-xs font-semibold", children: "ZIP *" }), _jsx("input", { autoComplete: "postal-code", inputMode: "numeric", className: "w-full rounded-md border bg-white px-3 py-2 text-sm", style: { borderColor: 'var(--bcbb-border)' }, value: service.postalCode, onChange: (e) => setService((a) => ({ ...a, postalCode: e.target.value })), required: true })] })] }), showMailing && (_jsxs(_Fragment, { children: [_jsxs("label", { className: "grid cursor-pointer grid-cols-[auto_1fr] items-start gap-2.5 text-sm", children: [_jsx("input", { type: "checkbox", className: "mt-1 h-4 w-4 accent-[var(--bcbb-primary)]", checked: sameAsService, onChange: (e) => setSameAsService(e.target.checked) }), _jsx("span", { children: "My mailing address is the same as my service address." })] }), !sameAsService && (_jsxs("fieldset", { className: "flex flex-col gap-4 rounded-md border border-[var(--bcbb-border)] bg-[var(--bcbb-surface-2)] p-4", children: [_jsx("legend", { className: "px-2 text-xs font-semibold text-[var(--bcbb-text-muted)]", children: "Mailing address" }), _jsx("input", { className: "w-full rounded-md border bg-white px-3 py-2 text-sm", style: { borderColor: 'var(--bcbb-border)' }, placeholder: "Address", value: mailing.line1, onChange: (e) => setMailing((a) => ({ ...a, line1: e.target.value })) }), _jsxs("div", { className: "grid grid-cols-1 gap-4 md:grid-cols-3", children: [_jsx("input", { className: "w-full rounded-md border bg-white px-3 py-2 text-sm", style: { borderColor: 'var(--bcbb-border)' }, placeholder: "City", value: mailing.city, onChange: (e) => setMailing((a) => ({ ...a, city: e.target.value })) }), _jsx("input", { className: "w-full rounded-md border bg-white px-3 py-2 text-sm", style: { borderColor: 'var(--bcbb-border)' }, placeholder: "State", value: mailing.state, onChange: (e) => setMailing((a) => ({ ...a, state: e.target.value.toUpperCase() })), maxLength: 2 }), _jsx("input", { className: "w-full rounded-md border bg-white px-3 py-2 text-sm", style: { borderColor: 'var(--bcbb-border)' }, placeholder: "ZIP", inputMode: "numeric", value: mailing.postalCode, onChange: (e) => setMailing((a) => ({ ...a, postalCode: e.target.value })) })] })] }))] }))] }));
}
export const AddressFieldArchetype = {
    name: 'AddressField',
    label: 'Address field (Mapbox)',
    description: 'Mapbox-autofill address with optional mailing toggle.',
    component: AddressFieldComponent,
    isFormArchetype: true,
    fields: {
        payloadKey: { kind: 'text', label: 'Service address payload key' },
        mailingPayloadKey: { kind: 'text', label: 'Mailing address payload key' },
        mailingToggleKey: { kind: 'text', label: 'Mailing-same flag payload key' },
        locationKeyPath: { kind: 'text', label: 'Location key payload key', optional: true },
        showMailing: { kind: 'checkbox', label: 'Show mailing toggle', defaultValue: true },
        country: { kind: 'text', label: 'Mapbox country (ISO)', optional: true },
    },
    defaultConfig: {
        payloadKey: 'serviceAddress',
        mailingPayloadKey: 'mailingAddress',
        mailingToggleKey: 'mailingSameAsService',
        locationKeyPath: 'locationKey',
        showMailing: true,
        country: 'us',
    },
};
